import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button, Card, Input } from '@driverforce/shared-ui';
import {
  TELEGRAM_RECIPIENT_ROLES,
  telegramRecipientInputSchema,
  type TelegramRecipient,
  type TelegramRecipientRole,
} from '@driverforce/shared-types';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Forbidden } from '../components/Forbidden';
import { toastApiError } from '../lib/apiToast';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; recipients: TelegramRecipient[] }
  | { kind: 'error' }
  | { kind: 'forbidden' };

export const TelegramSettingsPage: React.FC = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.roles.includes('admin') ?? false;

  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [chatId, setChatId] = useState('');
  const [role, setRole] = useState<TelegramRecipientRole>('admin');
  const [label, setLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!isAdmin) {
      setState({ kind: 'forbidden' });
      return;
    }
    setState({ kind: 'loading' });
    api.setToken(token);
    const res = await api.getTelegramRecipients();
    if (res.success && res.data) {
      setState({ kind: 'ready', recipients: res.data.recipients });
      return;
    }
    if (res.error?.code === 'FORBIDDEN') {
      setState({ kind: 'forbidden' });
      return;
    }
    toastApiError(res, 'Failed to load recipients');
    setState({ kind: 'error' });
  }, [isAdmin, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const addRecipient = useCallback(async () => {
    const parsed = telegramRecipientInputSchema.safeParse({
      chat_id: chatId,
      role,
      label: label.trim() || undefined,
    });
    if (!parsed.success) {
      toast.error('Invalid input', { description: parsed.error.issues[0]?.message ?? 'Check the chat id.' });
      return;
    }
    setAdding(true);
    api.setToken(token);
    const res = await api.addTelegramRecipient(parsed.data);
    setAdding(false);
    if (toastApiError(res, 'Failed to add recipient')) return;
    toast.success('Recipient added');
    setChatId('');
    setLabel('');
    await load();
  }, [chatId, role, label, token, load]);

  const removeRecipient = useCallback(
    async (id: string) => {
      setRemovingId(id);
      api.setToken(token);
      const res = await api.deleteTelegramRecipient(id);
      setRemovingId(null);
      if (toastApiError(res, 'Failed to remove recipient')) return;
      toast.success('Recipient removed');
      await load();
    },
    [token, load],
  );

  if (state.kind === 'forbidden') return <Forbidden />;

  return (
    <div className="max-w-2xl space-y-6">
      <Card title="Telegram notifications" padding="lg">
        <p className="text-sm text-gray-600 mb-4">
          Chats on this allowlist may use the bot. <strong>Admin</strong> chats also receive push
          notifications (new signups, uploads, reviews); <strong>viewer</strong> chats can query
          but are not pushed. To get a chat id, message the bot <code>/whoami</code>.
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <Input
              label="Chat ID"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder="123456789"
              inputMode="numeric"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as TelegramRecipientRole)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              {TELEGRAM_RECIPIENT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[140px]">
            <Input
              label="Label (optional)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Alex"
            />
          </div>
          <Button onClick={() => void addRecipient()} loading={adding} disabled={adding}>
            Add
          </Button>
        </div>
      </Card>

      <Card title="Recipients" padding="lg">
        {state.kind === 'loading' && <p className="text-sm text-gray-500">Loading…</p>}
        {state.kind === 'error' && (
          <p className="text-sm text-red-600">Could not load recipients. Try refreshing.</p>
        )}
        {state.kind === 'ready' && state.recipients.length === 0 && (
          <p className="text-sm text-gray-500">No recipients yet. Add your chat id above.</p>
        )}
        {state.kind === 'ready' && state.recipients.length > 0 && (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">Chat ID</th>
                <th className="py-2 pr-3">Role</th>
                <th className="py-2 pr-3">Label</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {state.recipients.map((r) => (
                <tr key={r.chat_id} className="border-b border-gray-100">
                  <td className="py-2 pr-3 font-mono">{r.chat_id}</td>
                  <td className="py-2 pr-3">{r.role}</td>
                  <td className="py-2 pr-3 text-gray-600">{r.label ?? '—'}</td>
                  <td className="py-2 text-right">
                    <Button
                      variant="danger"
                      size="sm"
                      loading={removingId === r.chat_id}
                      disabled={removingId === r.chat_id}
                      onClick={() => void removeRecipient(r.chat_id)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  );
};
